import { useEffect, useState } from "react";

const STILL = "(prefers-reduced-motion: reduce)";

interface RotatingWordProps {
  /** The words to cycle through, in the order they should appear. */
  words: readonly string[];
  /** How long a word holds the slot before the next one takes it. */
  holdMs?: number;
  className?: string;
}

/**
 * One word of a sentence, cycling through a short list of alternatives.
 *
 * Every word stays in the document, stacked into a single grid cell, which buys
 * two things a swap that unmounts cannot. The cell is as wide as the longest of
 * them and stays that way, so the sentence around it never moves: a slot that
 * resized to each word would drag the rest of a centred line back and forth
 * every few seconds, which is far more distracting than the word itself. And
 * the word on its way out is still there to be seen crossing the one on its way
 * in, rather than one replacing the other in a single frame.
 *
 * The slot is hidden from assistive technology. A word that changes on a timer
 * is noise to a screen reader, and read out halfway through a sentence it is
 * worse than noise, so the caller is expected to put the full phrase beside it
 * in text a screen reader can read once and be done with.
 */
export function RotatingWord({
  words,
  holdMs = 2600,
  className,
}: RotatingWordProps) {
  const [index, setIndex] = useState(0);
  const [still, setStill] = useState(false);

  /**
   * The word that just left is always the one before this one, because the
   * cycle only ever steps forward. On the first render that points at the last
   * word, which is parked out of sight anyway.
   */
  const leaving = (index + words.length - 1) % words.length;

  useEffect(() => {
    const query = window.matchMedia(STILL);
    const sync = () => setStill(query.matches);

    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  // Someone who asked for less motion gets the first word and nothing moving.
  useEffect(() => {
    if (still || words.length < 2) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % words.length);
    }, holdMs);

    return () => window.clearInterval(timer);
  }, [holdMs, still, words.length]);

  return (
    <span
      className={className ? `rotator ${className}` : "rotator"}
      aria-hidden="true"
    >
      {words.map((word, position) => {
        const state =
          position === index
            ? " rotator__word--in"
            : position === leaving
              ? " rotator__word--out"
              : "";

        return (
          <span key={word} className={`rotator__word${state}`}>
            {word}
          </span>
        );
      })}
    </span>
  );
}
